import React, { useEffect, useState } from 'react';
import ChevronDownSvg from '../../images/svg/chevron-down.svg';
import ChevronUpSvg from '../../images/svg/chevron-up.svg';
import { usePartnerConfig } from '../../contexts/PartnerConfig';
import { PartnerSlug } from '../../config/partnerConfigs';

type Props = {
  contentContainer: React.MutableRefObject<HTMLDivElement | null>;
  bottomRef: React.MutableRefObject<HTMLDivElement | null>;
};

export const ScrollToLayoutBottom = ({
  contentContainer,
  bottomRef,
}: Props): JSX.Element | null => {
  const [hasScrollBar, setHasScrollBar] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const partnerConfig = usePartnerConfig();
  const isHoiConfig = partnerConfig?.slug === PartnerSlug.HOI;

  useEffect(() => {
    const updateState = () => {
      const el = contentContainer.current;
      if (!el) return;

      // Check if scrollbar is needed
      const needsScrollBar =
        el.scrollHeight > el.getBoundingClientRect().height;
      setHasScrollBar(needsScrollBar);

      // Check if we're at the bottom (with small tolerance for floating point precision)
      const tolerance = 5;
      const atBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - tolerance;
      setIsAtBottom(atBottom);
    };

    const handleScroll = () => {
      const el = contentContainer.current;
      if (!el) return;

      const tolerance = 5;
      const atBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - tolerance;
      setIsAtBottom(atBottom);
    };

    updateState();

    const el = contentContainer.current;
    if (el) {
      el.addEventListener('scroll', handleScroll);
    }
    window.addEventListener('resize', updateState);

    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', updateState);
    };
  }, [contentContainer]);

  const handleClick = () => {
    const el = contentContainer.current;
    if (!el) return;

    if (isAtBottom) {
      // Scroll to top
      el.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Scroll to bottom
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (!hasScrollBar) {
    return null;
  }

  const scrollDownIconOverrides: React.CSSProperties = {};
  if (isHoiConfig) {
    scrollDownIconOverrides.backgroundColor = 'var(--hoi-blue)';
    scrollDownIconOverrides.color = 'white';
  }

  return (
    <div className="scrollDownButtonWrapper">
      <div
        className="scrollDownIcon"
        onClick={handleClick}
        style={scrollDownIconOverrides}
      >
        {isAtBottom ? <ChevronUpSvg /> : <ChevronDownSvg />}
      </div>
    </div>
  );
};
